import { useCallback, useMemo } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'

import { useOpenConversation } from '#/shared/lib/phone-panel'

import type { ConversationTarget } from './conversation'

// Re-export so the widget barrel keeps its public `useOpenConversation` surface;
// the opener itself now lives in shared (navigate-only) so lower layers can
// point the panel at a conversation without a cross-layer import cycle.
export { useOpenConversation }

type SearchRecord = Record<string, unknown>

interface ConversationControls {
  /** The open conversation, or null when none — derived from the URL. */
  target: ConversationTarget | null
  open: (target: ConversationTarget) => void
  close: () => void
  /** Drop the search-hit anchor and return the thread to its live tail. */
  clearAnchor: () => void
}

function str(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined
}

/**
 * The open phone-panel conversation, stored in the URL search params (`chat` =
 * conversation id, `chatTo` = counterparty number, `chatAt` = the search hit the
 * thread is anchored on). The URL is the single source of truth, so a reload —
 * or a copied link — reopens the same chat on the same message, and
 * `_dashboard` retains the params across page navigation.
 */
export function useConversationTarget(): ConversationControls {
  const raw: SearchRecord = useSearch({ strict: false })
  const navigate = useNavigate()
  const open = useOpenConversation()

  const chat = str(raw['chat'])
  const chatTo = str(raw['chatTo'])
  const chatAt = str(raw['chatAt'])
  const target = useMemo<ConversationTarget | null>(
    () =>
      (chat ?? chatTo)
        ? { conversationId: chat, phone: chatTo, messageId: chatAt }
        : null,
    [chat, chatTo, chatAt],
  )

  const close = useCallback(() => {
    // Set to undefined (keep the keys present) rather than delete — the
    // `_dashboard` `retainSearchParams` middleware re-adds keys that are absent,
    // which would otherwise block going back. Undefined values drop from the URL.
    void navigate({
      search: ((prev: SearchRecord) => ({
        ...prev,
        chat: undefined,
        chatTo: undefined,
        chatAt: undefined,
      })) as never,
    })
  }, [navigate])

  const clearAnchor = useCallback(() => {
    void navigate({
      search: ((prev: SearchRecord) => ({
        ...prev,
        chatAt: undefined,
      })) as never,
    })
  }, [navigate])

  return { target, open, close, clearAnchor }
}
